import { useRef, useState } from 'react';
import { PlacesType } from 'react-tooltip';
import { useTheme } from 'styled-components';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import * as S from './styles';

export enum SubmitButtonState {
	REGULAR,
	DISABLED,
}

interface SubmitButtonProps {
	// if state is not provided, defaults to SubmitButtonState.REGULAR
	state?: SubmitButtonState;

	type: IconType;

	// optional tooltip will appear on hover
	tooltip?: string;

	// optional placement of tooltip, can be
	// "top", "right", "bottom", or "left". defaults to "top"
	tooltipPlacement?: PlacesType;

	// this will be called when the button is clicked, if the state is not disabled
	onClick?: () => void;

	// optional custom colors for different button states
	colors?: {
		disabled?: string;
		hover?: string;
		active?: string;
	};
}

const SubmitButton = ({
	state,
	type,
	tooltip,
	tooltipPlacement,
	onClick,
	colors,
}: SubmitButtonProps) => {
	const [tooltipId] = useState(String(Math.random()));
	const ref = useRef(null);
	const theme = useTheme();

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onButtonPress = (event: any) => {
		// make sure we're clicking the button! (and not the tooltips, which also trigger this event)
		if (event.target === ref.current) {
			if (onClick && state !== SubmitButtonState.DISABLED) {
				// If we have an onClick handler, don't let the event propagate
				event?.stopPropagation();
				event?.preventDefault();
				onClick();
			}
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderBase = () => {
		return (
			<S.Base
				ref={ref}
				state={state || SubmitButtonState.REGULAR}
				colors={colors}
				onClick={onButtonPress}
			>
				<S.Icon>
					<Icon type={type} width={16} height={16} color={theme.gray['0']} />
				</S.Icon>
			</S.Base>
		);
	};

	if (tooltip) {
		const placement: PlacesType = tooltipPlacement ?? 'bottom';
		return (
			<>
				<a data-tooltip-id={tooltipId} data-tooltip-content={tooltip}>
					{renderBase()}
				</a>
				<Tooltip id={tooltipId} placement={placement} />
			</>
		);
	}
	return renderBase();
};

export default SubmitButton;
